/* HS!/JPD — configurator instrumentation.
 *
 * WHAT THIS IS NOT: it is not a pixel, and it deliberately loads nothing. No
 * Meta script, no Klaviyo script, no third-party request. It is a one-way event
 * bus that reports what the customer did to whatever measurement tools happen to
 * be on the page.
 *
 * WHY IT IS BUILT THIS WAY
 * ------------------------
 * The Meta pixel belongs in Shopify's Custom Pixels (Settings -> Customer
 * events), NOT in theme code, for three reasons that all bite this shop
 * specifically:
 *
 *   1. Consent. Shopify gates a custom pixel behind the Customer Privacy API on
 *      its own. A pixel pasted into a theme fires for everybody, everywhere,
 *      which is the shape of a regulatory problem in the EU and California.
 *   2. Theme churn. Writes to the live theme are blocked here - the flow is
 *      duplicate, edit, publish. A pixel living in theme code has to survive
 *      every one of those republishes. A custom pixel is stored on the SHOP and
 *      does not care which theme is live.
 *   3. Checkout. Theme code does not run on the checkout or thank-you pages, so
 *      a theme-embedded pixel cannot see a purchase - the one event that matters
 *      most. Shopify's own events can.
 *
 * So the split is: Shopify (or the Klaviyo app) owns loading and consent, and
 * this file owns describing what happened inside our custom configurators, which
 * Shopify cannot see because they are our own sections.
 *
 * EVERY SINK IS OPTIONAL. With no pixel installed at all this file is inert and
 * silent - it never throws, so instrumentation can never take the buy button
 * down with it. That is the whole reason each sink is wrapped.
 *
 * ZERO backslash literals in this file - the MCP deploy transport corrupts them.
 */
(function () {
  'use strict';

  // The funnel, in order. The point of numbering them is that "which step loses
  // people" is answerable by comparing counts of adjacent numbers; unnumbered
  // event names cannot be ordered after the fact without a lookup table nobody
  // maintains.
  var STEPS = {
    start: 1,        // engine booted, customer is looking at the configurator
    garment: 2,      // chose a cut / told us about their garment
    fit: 3,          // size, waist, inseam, or measurements
    fabric: 4,       // chose cloth
    work: 5,         // chose a technique or a service
    placement: 6,    // put a mark on the flat
    submit: 7,       // pressed the buy button
    added: 8         // the cart accepted it
  };

  function currency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) { /* fall through */ }
    return '';
  }

  // Shopify's own event stream. Custom Pixels subscribe to these by name, which
  // is how a Meta or Klaviyo event gets fired without this file knowing either
  // vendor exists. Absent on non-Shopify pages (the local harness), hence guard.
  function toShopify(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* measurement must never break the page */ }
  }

  // If a pixel IS present in the page despite the advice above - an app injected
  // it, or somebody pasted it - report to it directly rather than silently
  // dropping the event.
  function toMeta(name, payload) {
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', name, payload);
      }
    } catch (e) { /* ignore */ }
  }

  function toKlaviyo(name, payload) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', name, payload]);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', name, payload]);   // older onsite snippet
      }
    } catch (e) { /* ignore */ }
  }

  // A queue anything else can read, and the thing that makes this debuggable:
  // open the console on a real page and read window.HSTrack.log().
  var LOG = [];

  function emit(name, payload) {
    var body = payload || {};
    if (!body.currency) body.currency = currency();
    LOG.push({ event: name, payload: body });
    if (LOG.length > 200) LOG.shift();      // a bounded log cannot leak memory
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, hs: body });
    } catch (e) { /* ignore */ }
    toShopify(name, body);
    toMeta(name, body);
    toKlaviyo(name, body);
  }

  // Each step fires AT MOST ONCE per engine instance. Without this, ticking four
  // add-ons would report four "work" steps and the funnel would read as though
  // more people reached step 5 than reached step 4.
  function makeTracker(surface) {
    var seen = {};
    return {
      step: function (key, extra) {
        if (!STEPS[key] || seen[key]) return;
        seen[key] = true;
        var body = extra || {};
        body.surface = surface;
        body.step = STEPS[key];
        body.step_name = key;
        emit('hs_step', body);
      },
      // Not a step - these can legitimately repeat, and a second attempt after a
      // failure is exactly the signal worth keeping.
      event: function (name, extra) {
        var body = extra || {};
        body.surface = surface;
        emit(name, body);
      },
      reset: function () { seen = {}; }
    };
  }

  window.HSTrack = {
    STEPS: STEPS,
    emit: emit,
    for: makeTracker,
    log: function () { return LOG.slice(); }
  };
})();
